import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import i18n from '@/lib/i18n';
import { LANGUAGE_STORAGE_KEY } from '@/lib/currency';
import { api } from '@/lib/api';

export interface User {
  id: string;
  email: string;
  full_name: string;
  role: string;
  phone?: string;
  status: 'active' | 'inactive' | 'pending_deletion' | 'archived' | 'anonymized';
  // Resident hat: holds a unit role (PRD Epic E).
  // Modes explicitly available to this user
  available_modes?: string[];
  is_2fa_enabled?: boolean;
  password_changed_at?: string | null;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  /** Persisted UI language; null means "follow the browser". */
  preferred_language?: string | null;
}

/**
 * Apply the server's language preference over the localStorage cache.
 *
 * Without this, a user's choice was per-browser: signing in on another device
 * showed the previous language. Called on login and on every /auth/me refresh.
 * A null preference means "follow the browser", so localStorage is left alone.
 */
function syncLanguagePreference(user: { preferred_language?: string | null } | null): void {
  const preferred = user?.preferred_language;
  if (!preferred) return;
  const current = i18n.resolvedLanguage || i18n.language || 'en';
  if (current === preferred) return;
  i18n.changeLanguage(preferred);
  localStorage.setItem(LANGUAGE_STORAGE_KEY, preferred);
}


interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isImpersonating: boolean;
  impersonatedByName: string | null;
  appMode: string | null;
  /** True once the stored/default app mode has been reconciled with the user's hats. */
  appModeResolved: boolean;
  setAppMode: (mode: string) => void;
  login: (token: string, user: User) => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
  startImpersonation: (token: string, targetUser: User) => void;
  exitImpersonation: () => void;
}


const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(() => {
    const saved = localStorage.getItem('koinon_user');
    return saved ? JSON.parse(saved) : null;
  });
  const [token, setToken] = useState<string | null>(() => {
    return localStorage.getItem('koinon_token');
  });
  const [isImpersonating, setIsImpersonating] = useState<boolean>(() => {
    return Boolean(localStorage.getItem('koinon_superadmin_token'));
  });
  const [impersonatedByName, setImpersonatedByName] = useState<string | null>(() => {
    const savedAdmin = localStorage.getItem('koinon_superadmin_user');
    if (savedAdmin) {
      try {
        return JSON.parse(savedAdmin).full_name;
      } catch {
        return 'Super Admin';
      }
    }
    return null;
  });
  const [isLoading, setIsLoading] = useState(true);

  const [appMode, setAppModeState] = useState<string | null>(() => {
    try {
      const stored = localStorage.getItem('koinon_app_mode');
      if (!stored) return null;
      const userJson = localStorage.getItem('koinon_user');
      if (userJson) {
        const modes = JSON.parse(userJson).available_modes;
        // A stale stored mode the user no longer holds must not win: fall
        // through to the default-pick effect below instead of flashing it.
        if (modes && !modes.includes(stored)) return null;
      }
      return stored;
    } catch {
      return localStorage.getItem('koinon_app_mode');
    }
  });
  const [appModeResolved, setAppModeResolved] = useState(false);

  const setAppMode = useCallback((mode: string) => {
    setAppModeState(mode);
    localStorage.setItem('koinon_app_mode', mode);
  }, []);

  useEffect(() => {
    if (user && user.available_modes && user.available_modes.length > 0) {
      if (!appMode || !user.available_modes.includes(appMode)) {
        setAppMode(user.available_modes[0]);
      }
    }
    setAppModeResolved(true);
  }, [user, appMode, setAppMode]);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setIsImpersonating(false);
    setImpersonatedByName(null);
    setAppModeState(null);
    localStorage.removeItem('koinon_token');
    localStorage.removeItem('koinon_user');
    localStorage.removeItem('koinon_superadmin_token');
    localStorage.removeItem('koinon_superadmin_user');
    localStorage.removeItem('koinon_app_mode');
    delete api.defaults.headers.common['Authorization'];
    queryClient.clear();
  }, [queryClient]);

  const login = useCallback((newToken: string, newUser: User) => {
    setToken(newToken);
    setUser(newUser);
    localStorage.setItem('koinon_token', newToken);
    localStorage.setItem('koinon_user', JSON.stringify(newUser));
    api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    queryClient.clear();
    syncLanguagePreference(newUser);
  }, [queryClient]);

  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Verify token is valid with /auth/me
      api.get('/auth/me')
        .then((res) => {
          setUser(res.data);
          localStorage.setItem('koinon_user', JSON.stringify(res.data));
        })
        .catch(() => {
          logout();
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      delete api.defaults.headers.common['Authorization'];
      setIsLoading(false);
    }
  }, [token, logout]);

  const startImpersonation = useCallback((targetToken: string, targetUser: User) => {
    if (token && user) {
      localStorage.setItem('koinon_superadmin_token', token);
      localStorage.setItem('koinon_superadmin_user', JSON.stringify(user));
      setImpersonatedByName(user.full_name);
    }
    setIsImpersonating(true);
    login(targetToken, targetUser);
  }, [token, user, login]);

  const exitImpersonation = useCallback(() => {
    const origToken = localStorage.getItem('koinon_superadmin_token');
    const origUserJson = localStorage.getItem('koinon_superadmin_user');
    if (origToken && origUserJson) {
      const origUser = JSON.parse(origUserJson);
      localStorage.removeItem('koinon_superadmin_token');
      localStorage.removeItem('koinon_superadmin_user');
      setIsImpersonating(false);
      setImpersonatedByName(null);
      login(origToken, origUser);
      window.location.href = '/superadmin';
    } else {
      logout();
    }
  }, [login, logout]);


  const refreshUser = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
      localStorage.setItem('koinon_user', JSON.stringify(res.data));
      syncLanguagePreference(res.data);
    } catch {
      // Ignore
    }
  }, []);

  const contextValue: AuthContextType = useMemo(
    () => ({
      user,
      token,
      isAuthenticated: Boolean(token && user),
      isLoading,
      isImpersonating,
      impersonatedByName,
      appMode,
      appModeResolved,
      setAppMode,
      login,
      logout,
      refreshUser,
      startImpersonation,
      exitImpersonation,
    }),
    [
      user,
      token,
      isLoading,
      isImpersonating,
      impersonatedByName,
      appMode,
      appModeResolved,
      setAppMode,
      login,
      logout,
      refreshUser,
      startImpersonation,
      exitImpersonation,
    ]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};



  // eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
